import { createRootRoute, createRoute, createRouter, Outlet } from '@tanstack/react-router'
import { validateScanSearch } from './lib/link'
import { LegacyScanPage, ScanCodePage } from './routes/scan-page'
import { NotFoundPage } from './routes/not-found-page'

/**
 * `notFoundComponent` is on the ROOT, and it is not decoration.
 *
 * The bare origin - `https://scan.dev.us.predixa.health/` - became a page somebody reaches the day
 * the credential became the path: a patient deletes the code off the end of the URL, or is told the
 * hostname and nothing else. It matches neither `$code` (there is no segment) nor the splat route (a
 * splat needs something to match), so without this it renders TanStack Router's own built-in "Not
 * Found" string - unstyled, in English, from a library rather than from this app. This makes the one
 * page this app wrote for "there is nothing here" the answer to every such path.
 */
const rootRoute = createRootRoute({
  component: () => <Outlet />,
  notFoundComponent: NotFoundPage,
})

/**
 * `{WebappBaseUrl}/{code}` — THE SHORT CODE IS THE WHOLE PATH (docs/decisions.md D-14, D-41).
 *
 * The link is delivered by SMS, where every character is billed and the receiving handset decides
 * where to wrap. `/v2/scan?token=` in front of an eight-character credential was fourteen characters
 * of this app's own routing in a message that is read aloud down a phone, so it is gone:
 * `https://scan.dev.us.predixa.health/GB7GMEGK`.
 *
 * A dynamic single-segment route rather than a splat, and that is what keeps the real objects on
 * this origin reachable. `/assets/*` and `/sdk/*` are nested and `$code` cannot match a `/`, so they
 * can never be read as a credential even before the page loads; `/config.json` and
 * `/predixa-logo.png` are real S3 keys and never reach the router at all, because CloudFront serves
 * them from the bucket and only rewrites to `/index.html` on the 403 an ABSENT key produces
 * (terraform/cloudfront.tf). What is left for this route is exactly the set of paths that have no
 * object behind them, which is the set a credential lives in. ScanCodePage decides whether the
 * segment it got looks like a credential; a segment that does not renders the not-found page.
 *
 * Route ranking settles the rest: `/v2/scan` is static and two segments deep, so it is matched
 * before `$code` is ever considered, and `*` catches everything with more than one segment.
 */
const codeRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/$code',
  component: ScanCodePage,
})

/**
 * `{WebappBaseUrl}/v2/scan?token={linkToken}` — the shape every link already sent carries.
 *
 * **It stays until the links that use it cannot work anyway.** A scan grant lives eight hours
 * (`TokenLifetime`), and an SMS sits in an inbox for as long as the patient leaves it there, so the
 * last old-shape link stops being openable eight hours after the last deploy that minted one. This
 * route may be deleted, together with `parseScanLink`/`validateScanSearch` and rows 224-227, 286-287
 * and 438, once the release carrying D-41 has been applied to every installation (us-dev, us-stg,
 * us-prd) for a full day — one day rather than eight hours because installations are not applied at
 * the same moment and nobody should have to reason about which one is behind.
 *
 * It RESOLVES the flow rather than redirecting to `/{code}`: the old parameter can carry the
 * pre-D-14 dotted token, which `parseScanCode` refuses by design, so a redirect would break exactly
 * the links this route exists for. See routes/scan-page.tsx.
 *
 * `token` is required by nothing here; a missing one is not a routing concern, it is the page's first
 * real screen, so validateSearch only shapes the type. It delegates to lib/link's validateScanSearch
 * rather than parsing the query string a second time here. This file used to do the parsing itself,
 * which left parseScanLink with no caller outside its own tests - four rows describing link parsing
 * the shipped app never performed - and the two disagreed on a whitespace-only token: this file
 * accepted one, and the patient met the dead-link screen three screens later instead of the
 * missing-token one immediately.
 */
const scanRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/v2/scan',
  validateSearch: validateScanSearch,
  component: LegacyScanPage,
})

const notFoundRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '*',
  component: NotFoundPage,
})

/**
 * Exported so a test can mount the REAL route tree against a memory history rather than describing
 * the routing in prose. The `router` below is the app's one instance of it; nothing about which
 * component answers which path lives anywhere else.
 */
export const routeTree = rootRoute.addChildren([scanRoute, codeRoute, notFoundRoute])

export const router = createRouter({ routeTree })

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router
  }
}
